import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { 
    getLeadAccounts, 
    deleteLeadAccount, 
    patchLeadAccount 
} from '../api';
import { logError } from '../api';
import GenericList from './Global/GenericList';
import LeadAccountSearchForm from './LeadAccountSearchForm';

import DisabledToggle from './ui/DisabledToggle';
import { useDisabledToggle } from '../hooks/useDisabledToggle';
import { useSorting } from '../hooks/useSorting';
import useFilterPersistence from '../hooks/useFilterPersistence';

const LeadAccountList = () => {
    const [leadAccounts, setLeadAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    const [searchFilters, setSearchFilters] = useFilterPersistence('filters_lead_accounts', {});
    const [pagination, setPagination] = useFilterPersistence('pagination_lead_accounts', { page: 1, pageSize: 50 });
    const currentPage = pagination.page;
    const itemsPerPage = pagination.pageSize;

    const { includeDisabled, setIncludeDisabled } = useDisabledToggle();
    const { orderBy, orderDesc, handleSort } = useSorting();
    const navigate = useNavigate();

    const loadLeadAccounts = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getLeadAccounts(
                currentPage, 
                itemsPerPage, 
                includeDisabled, 
                orderBy, 
                orderDesc, 
                searchFilters
            );
            setLeadAccounts(response.items || []);
            setTotalCount(response.totalCount || 0);
        } catch (error) {
            logError(error);
            console.error('Error loading lead accounts:', error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, itemsPerPage, includeDisabled, orderBy, orderDesc, searchFilters]);

    useEffect(() => {
        loadLeadAccounts();
    }, [loadLeadAccounts]);

    const handleDelete = async (leadAccount) => {
        try {
            await deleteLeadAccount(leadAccount.id);
            loadLeadAccounts();
        } catch (error) {
            logError(error);
            console.error('Error deleting lead account:', error);
        }
    };

    const handleEdit = (leadAccount) => {
        navigate(`/lead-accounts/edit/${leadAccount.id}`);
    };

    const handleToggleStatus = async (leadAccountId, currentStatus) => {
        try {
            await patchLeadAccount(leadAccountId, { disabled: !currentStatus });
            loadLeadAccounts();
        } catch (error) {
            logError(error);
            console.error('Error toggling lead account status:', error);
        }
    };

    const handleSearch = (filters) => {
        setSearchFilters(filters);
        setPagination({ ...pagination, page: 1 });
    };

    const columns = [
        { key: 'id', header: 'ID', sortable: true },
        {
            key: 'assignedLead',
            header: 'Lead Asignado',
            sortable: false,
            render: (leadAccount) => leadAccount.assignedLead?.username || ''
        },
        { key: 'name', header: 'Nombre', sortable: true },
        { key: 'email', header: 'Email', sortable: true },
        { key: 'zipCode', header: 'Código Postal', sortable: true },
        {
            key: 'leadSource',
            header: 'Fuente de Lead',
            sortable: true,
            render: (leadAccount) => leadAccount.leadSource?.name || ''
        },

    ];

    return (
        <Container fluid className="my-4">
            <Row className="mb-3">
                <Col xs={12} className="d-flex justify-content-end">
                    <DisabledToggle 
                        includeDisabled={includeDisabled}
                        onChange={setIncludeDisabled}
                    />
                </Col>
            </Row>
            <GenericList
                title="Cuentas de Lead"
                data={leadAccounts}
                columns={columns}
                loading={loading}
                totalCount={totalCount}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => setPagination({ ...pagination, page })}
                onItemsPerPageChange={(pageSize) => setPagination({ page: 1, pageSize })}
                onDelete={handleDelete}
                onEdit={handleEdit}
                onToggleStatus={handleToggleStatus}
                createRoute="/lead-accounts/new"
                createButtonText="Nueva Cuenta de Lead"
                deleteMessage="¿Estás seguro de que deseas eliminar esta cuenta de lead?"
                emptyMessage="No se encontraron cuentas de lead."
                onSort={handleSort}
                sortable={true}
                containerFluid={true}
                showSearch={true}
                SearchComponent={LeadAccountSearchForm}
                searchProps={{
                    onSearch: handleSearch,
                    initialFilters: searchFilters
                }}

            />
        </Container>
    );
};

export default LeadAccountList;